import { useViewerHasAtLeastCustomerSupportRights } from "@/lib/rights/use-viewer-has-right";
import { isLineItemRejected } from "@/pages/(app)/returns/view/utils/get-line-items-grouped-by-categories";
import { getLineItemNotTreatedByMerchantQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-merchant-quantity";
import {
  ProductVariantDto,
  ReturnRequestLineItemDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { ArrowLeftRight } from "lucide-react";
import { useMemo, useState } from "react";
import { ExchangeVariantOverrideModal } from "../exchange-variant-override-modal/exchange-variant-override-modal";
import ArticleItemRejectReason from "./article-item-reject-reason";
import { ArticleItemVariant } from "./article-item-variant";

type ArticleItemSublineProps = {
  lineItem: ReturnRequestLineItemDto;
  onChangeVariant?: (variant: ProductVariantDto) => void;
  hideReturnReason?: boolean;
  hideRejectReason?: boolean;
  hideStatus?: boolean;
  hideComment?: boolean;
  hideImages?: boolean;
  hideVariant?: boolean;
};

export function ArticleItemSubline({
  lineItem,
  hideVariant,
  hideReturnReason,
  hideRejectReason,
  hideImages,
  hideComment,
  onChangeVariant,
}: ArticleItemSublineProps) {
  const t = useTranslation();
  const hasRightToEditVariant = useViewerHasAtLeastCustomerSupportRights();
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);

  const canEditVariant =
    hasRightToEditVariant &&
    !!onChangeVariant &&
    lineItem.resolution === "exchange";

  const canOverrideVariantFromCatalog =
    canEditVariant && getLineItemNotTreatedByMerchantQuantity({ lineItem }) > 0;

  const items = useMemo(() => {
    return (
      [
        lineItem.sku && (
          <span key={"article-item-sku"} className="text-muted-foreground">
            {lineItem.sku}
          </span>
        ),
        !hideVariant && (
          <ArticleItemVariant
            key={"article-item-variant"}
            lineItem={lineItem}
            editable={canEditVariant}
            onChange={onChangeVariant}
          />
        ),
        !hideVariant && canOverrideVariantFromCatalog && (
          <Tooltip key={"article-item-variant-override"}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon-xs"
                onClick={() => setOverrideModalOpen(true)}
                aria-label={t("returnDetail.exchangeVariantOverride.trigger")}
                className="[&_svg]:size-3.5"
              >
                <ArrowLeftRight />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {t("returnDetail.exchangeVariantOverride.trigger")}
            </TooltipContent>
          </Tooltip>
        ),
        lineItem.new_variant?.sku && (
          <span key={"article-item-sku"} className="text-muted-foreground">
            {lineItem.new_variant.sku}
          </span>
        ),
        !hideRejectReason && isLineItemRejected(lineItem) && (
          <span key={"article-item-reject-reason"} className="text-destructive">
            <ArticleItemRejectReason lineItem={lineItem} />
          </span>
        ),
      ].filter(Boolean) as React.ReactNode[]
    )
      .reduce<React.ReactNode[]>(
        (prev, curr, index) => [
          ...prev,
          curr,
          <span key={`separator-${index}`}>·</span>,
        ],
        [],
      )
      .slice(0, -1);
  }, [
    lineItem,
    hideVariant,
    canEditVariant,
    canOverrideVariantFromCatalog,
    onChangeVariant,
    hideReturnReason,
    hideRejectReason,
    hideImages,
    hideComment,
    t,
  ]);

  return (
    <>
      <span className="text-xs flex-wrap text-muted-foreground inline-flex gap-2 items-center flex-1">
        {items}
      </span>
      {canOverrideVariantFromCatalog && onChangeVariant && (
        <ExchangeVariantOverrideModal
          open={overrideModalOpen}
          onOpenChange={setOverrideModalOpen}
          currentVariantId={
            lineItem.new_variant?.variant_id ?? lineItem.variant.variant_id
          }
          currentVariantPrice={lineItem.paidPrice ?? lineItem.price}
          onConfirm={async (variant) => {
            await onChangeVariant(variant);
          }}
        />
      )}
    </>
  );
}
